import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { mealPlansAPI } from "../../services/api";
import MealPlanForm from "./MealPlanForm";
import toast from "react-hot-toast";
import LoadingSpinner from "../../components/LoadingSpinner";

const EditMealPlan = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();

  // Fetch the meal plan to edit
  const {
    data: mealPlanData,
    isLoading,
    error,
  } = useQuery(
    ["vendorMealPlan", id],
    () => mealPlansAPI.getVendorMealPlan(id),
    {
      enabled: !!id,
      retry: false, // Don't retry on error
    }
  );

  const updateMealPlanMutation = useMutation(
    (data) => mealPlansAPI.updateMealPlan(id, data),
    {
      onSuccess: (data) => {
        toast.success("Meal plan updated successfully!");
        queryClient.invalidateQueries("vendor-meal-plans");
        queryClient.invalidateQueries(["vendorMealPlan", id]);
        navigate("/vendor/meal-plans");
      },
      onError: (error) => {
        console.error("Error updating meal plan:", error);
        toast.error(
          error.response?.data?.message || "Failed to update meal plan"
        );
      },
    }
  );

  const handleSave = async (formData) => {
    try {
      await updateMealPlanMutation.mutateAsync(formData);
    } catch (error) {
      // Error is handled by the mutation's onError callback
      throw error;
    }
  };

  const handleCancel = () => {
    navigate("/vendor/meal-plans");
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h3>Error loading meal plan</h3>
        <p>{error.message || "Failed to load meal plan"}</p>
        <button onClick={() => navigate("/vendor/meal-plans")}>
          Back to Meal Plans
        </button>
      </div>
    );
  }

  if (!mealPlanData?.data?.data?.mealPlan) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h3>Meal plan not found</h3>
        <p>
          The meal plan you're looking for doesn't exist or you don't have
          permission to edit it.
        </p>
        <button onClick={() => navigate("/vendor/meal-plans")}>
          Back to Meal Plans
        </button>
      </div>
    );
  }

  return (
    <MealPlanForm
      mealPlan={mealPlanData.data.data.mealPlan}
      onSave={handleSave}
      onCancel={handleCancel}
    />
  );
};

export default EditMealPlan;
